import React from "react";
import PropTypes from "prop-types";
import { Empty } from "antd";
import SectionBlock from "./components/section-block";
import CardBlock from "./components/card-block";
import { SECTION_IDS } from "./constants";
import AccountFacilityDetails from "../account-facility-tab";

/**
 * Pricing — ROI / Tenure / Subvention / Margin / SD, sourced from the case's
 * active supply-chain scheme. Same view-model as the credit brief PDF, so the
 * on-screen tiles and the PDF tiles stay in lockstep. Read-only; edits still
 * go through the scheme master.
 *
 * Below the pricing tiles the full Account / Facility Details tab is embedded
 * (the SAME connected component the standalone "Account/Facility Details" tab
 * renders) so the credit team can create/edit facilities, processing fee,
 * schedules, skip-POD, etc. without leaving Credit Checker. Because it is the
 * same component, every API call and action is preserved verbatim — only the
 * surrounding chrome (credit-checker SectionBlock / CardBlock) differs.
 */
const PricingSection = ({
    innerRef,
    expanded,
    onToggle,
    pricing,
    caseId,
    companyId,
    caseDetail,
    stageId,
    isUserAllowedToEdit,
    recreditViewOnlyPermission,
    location,
    companyName,
    stageName
}) => (
    <SectionBlock
        id={SECTION_IDS.PRICING}
        innerRef={innerRef}
        title={"Pricing"}
        icon={"tag"}
        expanded={expanded}
        onToggle={onToggle}
    >
        {pricing && pricing.length ? (
            <div className={"cc-pricing"}>
                {pricing.map(row => (
                    <div className={"cc-pricing__tile"} key={row.label}>
                        <div className={"cc-pricing__label"}>{row.label}</div>
                        <div className={"cc-pricing__value"}>{row.value}</div>
                    </div>
                ))}
            </div>
        ) : (
            <Empty description={"Pricing not available for this scheme"} />
        )}

        {/* Full Account / Facility Details — reused connected component, wrapped
            in a credit-checker CardBlock so it reads as part of Pricing while
            keeping all of its own functionality and API calls intact. */}
        {caseDetail ? (
            <CardBlock
                icon={"bank"}
                title={"Account / Facility Details"}
                className={"cc-account-facility"}
                collapsible
                defaultCollapsed
            >
                <AccountFacilityDetails
                    caseId={caseId}
                    companyId={companyId}
                    caseDetail={caseDetail}
                    stageId={stageId}
                    isUserAllowedToEdit={isUserAllowedToEdit}
                    recreditViewOnlyPermission={recreditViewOnlyPermission}
                    location={location}
                    companyName={companyName}
                    stageName={stageName}
                />
            </CardBlock>
        ) : null}
    </SectionBlock>
);

PricingSection.propTypes = {
    innerRef: PropTypes.object,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    // [{ label, value }] — produced by data-utils.buildPricing.
    pricing: PropTypes.array,
    // Forwarded to the embedded Account / Facility Details component.
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    caseDetail: PropTypes.object,
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isUserAllowedToEdit: PropTypes.bool,
    recreditViewOnlyPermission: PropTypes.bool,
    location: PropTypes.object,
    companyName: PropTypes.string,
    stageName: PropTypes.string
};

export default PricingSection;
